import React, { useMemo } from 'react';
import { Stack, Typography, Box, Button, Alert, Chip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Papa from 'papaparse';
import { toast } from 'react-toastify';

import { DenseTable, DenseColumn } from '../../../../components/groups-teams/shared/DenseTable';
import { GT_COLORS } from '../../../../constants/designTokens.constants';
import { BulkUpdatePreviewResponse, UpdateFieldDiff } from '../../../../types/bulkActions.types';
import { formatTimeEstimate } from '../delete-users/timeEstimate';

type PreviewStatus = 'ready' | 'no-op' | 'validation-error' | 'unmatched' | 'duplicate';

interface PreviewTableRow {
    rowNumber: number;
    status: PreviewStatus;
    identifierValue: string;
    name: string;
    changeSummary: string;
    _key: string;
}

const statusChipProps: Record<PreviewStatus, { label: string; bg: string; color: string }> = {
    ready: { label: 'Ready', bg: GT_COLORS.greenLight, color: GT_COLORS.green },
    'no-op': { label: 'No Change', bg: GT_COLORS.badgeGrayBg, color: GT_COLORS.badgeGrayColor },
    'validation-error': { label: 'Error', bg: GT_COLORS.redLight, color: GT_COLORS.red },
    unmatched: { label: 'Unmatched', bg: GT_COLORS.badgeGrayBg, color: GT_COLORS.badgeGrayColor },
    duplicate: { label: 'Duplicate', bg: GT_COLORS.amberLight, color: GT_COLORS.amber },
};

const FIELD_LABELS: Record<string, string> = {
    firstName: 'First Name',
    lastName: 'Last Name',
    backupEmail: 'Email',
    phoneNumber: 'Phone',
    employeeId: 'Employee ID',
    teamName: 'Team',
    tags: 'Tags',
};

const formatDiffSummary = (diffs: UpdateFieldDiff[]): string => {
    return diffs.map(d => {
        const label = FIELD_LABELS[d.field] || d.field;
        const oldVal = d.oldValue || '(empty)';
        const newVal = d.newValue === null ? '(cleared)' : d.newValue;
        return `${label}: ${oldVal} \u2192 ${newVal}`;
    }).join('; ');
};

interface PreviewStepProps {
    previewData: BulkUpdatePreviewResponse;
    onContinue: () => void;
    onBack: () => void;
}

export const PreviewStep: React.FC<PreviewStepProps> = ({
    previewData,
    onContinue,
    onBack,
}) => {
    const { readyToUpdate, noOps, validationErrors, unmatched, summary } = previewData;

    const tableRows = useMemo<PreviewTableRow[]>(() => {
        const rows: PreviewTableRow[] = [];

        for (const row of readyToUpdate) {
            const snapshot = row.userSnapshot;
            rows.push({
                rowNumber: row.rowNumber,
                status: 'ready',
                identifierValue: row.providedFields[previewData.identifierField] || '',
                name: [snapshot.firstName, snapshot.lastName].filter(Boolean).join(' ') || '-',
                changeSummary: formatDiffSummary(row.diffs),
                _key: `ready-${row.rowNumber}`,
            });
        }

        for (const row of noOps) {
            const snapshot = row.userSnapshot;
            rows.push({
                rowNumber: row.rowNumber,
                status: 'no-op',
                identifierValue: row.providedFields[previewData.identifierField] || '',
                name: [snapshot.firstName, snapshot.lastName].filter(Boolean).join(' ') || '-',
                changeSummary: 'No changes',
                _key: `noop-${row.rowNumber}`,
            });
        }

        for (const row of validationErrors) {
            const snapshot = row.userSnapshot;
            rows.push({
                rowNumber: row.rowNumber,
                status: 'validation-error',
                identifierValue: row.providedFields[previewData.identifierField] || '',
                name: [snapshot.firstName, snapshot.lastName].filter(Boolean).join(' ') || '-',
                changeSummary: row.errors.join('; '),
                _key: `error-${row.rowNumber}`,
            });
        }

        for (const row of unmatched) {
            const isDuplicate = row.reason === 'duplicate identifier in upload';
            rows.push({
                rowNumber: row.rowNumber,
                status: isDuplicate ? 'duplicate' : 'unmatched',
                identifierValue: row.identifierValue,
                name: [row.providedFields.first_name, row.providedFields.last_name].filter(Boolean).join(' ') || '-',
                changeSummary: row.reason.charAt(0).toUpperCase() + row.reason.slice(1),
                _key: `unmatched-${row.rowNumber}`,
            });
        }

        rows.sort((a, b) => a.rowNumber - b.rowNumber);
        return rows;
    }, [readyToUpdate, noOps, validationErrors, unmatched, previewData.identifierField]);

    const canContinue = summary.readyToUpdateCount > 0;

    const columns: DenseColumn<PreviewTableRow>[] = [
        {
            id: 'rowNumber',
            label: 'Row #',
            width: 60,
            sortable: true,
            getSortValue: (row) => row.rowNumber,
            render: (row) => (
                <Typography fontSize={13} color={GT_COLORS.textMuted}>{row.rowNumber}</Typography>
            ),
        },
        {
            id: 'status',
            label: 'Status',
            width: 130,
            sortable: true,
            getSortValue: (row) => {
                const order: Record<PreviewStatus, number> = {
                    'ready': 0,
                    'validation-error': 4,
                    'duplicate': 2,
                    'unmatched': 3,
                    'no-op': 1,
                };
                return order[row.status] ?? 5;
            },
            render: (row) => {
                const chip = statusChipProps[row.status];
                return (
                    <Chip
                        label={chip.label}
                        size="small"
                        sx={{
                            backgroundColor: chip.bg,
                            color: chip.color,
                            fontWeight: 500,
                            fontSize: 12,
                            height: 24,
                        }}
                    />
                );
            },
        },
        {
            id: 'identifier',
            label: 'Identifier',
            width: 200,
            sortable: true,
            getSortValue: (row) => row.identifierValue,
            render: (row) => (
                <Typography fontSize={13} noWrap>{row.identifierValue}</Typography>
            ),
        },
        {
            id: 'name',
            label: 'Name',
            width: 180,
            sortable: true,
            getSortValue: (row) => row.name,
            render: (row) => (
                <Typography fontSize={13}>{row.name}</Typography>
            ),
        },
        {
            id: 'changes',
            label: 'Changes',
            render: (row) => {
                let color: string = GT_COLORS.textMuted;
                if (row.status === 'ready') color = GT_COLORS.green;
                if (row.status === 'validation-error') color = GT_COLORS.red;
                return (
                    <Typography fontSize={13} color={color} sx={{ wordBreak: 'break-word' }}>
                        {row.changeSummary}
                    </Typography>
                );
            },
        },
    ];

    const handleDownloadPreview = () => {
        try {
            const csvData = tableRows.map(row => ({
                'Row Number': row.rowNumber,
                'Status': statusChipProps[row.status].label,
                'Identifier': row.identifierValue,
                'Name': row.name,
                'Changes': row.changeSummary,
            }));
            const csvContent = Papa.unparse(csvData);
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'bulk-update-preview.csv';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error('Failed to download preview.');
        }
    };

    const nonDuplicateUnmatched = summary.unmatchedCount - summary.duplicateIdentifierCount;

    const summaryBoxes = [
        { label: 'Total Rows', value: summary.totalRows, bg: GT_COLORS.headerBg, color: GT_COLORS.textPrimary },
        { label: 'Ready to Update', value: summary.readyToUpdateCount, bg: GT_COLORS.greenBg, color: GT_COLORS.green },
        { label: 'No Change', value: summary.noOpCount, bg: GT_COLORS.headerBg, color: GT_COLORS.textSecondary },
        { label: 'Errors', value: summary.validationErrorCount, bg: summary.validationErrorCount > 0 ? GT_COLORS.redBg : GT_COLORS.headerBg, color: summary.validationErrorCount > 0 ? GT_COLORS.red : GT_COLORS.textSecondary },
        { label: 'Unmatched', value: nonDuplicateUnmatched, bg: GT_COLORS.headerBg, color: GT_COLORS.textSecondary },
        ...(summary.duplicateIdentifierCount > 0
            ? [{ label: 'Duplicates', value: summary.duplicateIdentifierCount, bg: GT_COLORS.amberBg, color: GT_COLORS.amber }]
            : []),
    ];

    const estimate = formatTimeEstimate(summary.readyToUpdateCount);

    return (
        <Stack spacing={3}>
            {/* Summary stat boxes */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {summaryBoxes.map((box) => (
                    <Box
                        key={box.label}
                        sx={{
                            flex: '1 1 140px',
                            p: 2,
                            borderRadius: '8px',
                            backgroundColor: box.bg,
                            textAlign: 'center',
                        }}
                    >
                        <Typography fontSize={22} fontWeight={700} color={box.color}>
                            {box.value}
                        </Typography>
                        <Typography fontSize={12} color={GT_COLORS.textMuted}>
                            {box.label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* New tags info */}
            {summary.newTagNames.length > 0 && (
                <Alert severity="info" sx={{ borderRadius: '8px' }}>
                    <Typography fontSize={14}>
                        <strong>{summary.newTagNames.length} new tag{summary.newTagNames.length !== 1 ? 's' : ''}</strong> will be created: {summary.newTagNames.join(', ')}
                    </Typography>
                </Alert>
            )}

            {/* Preview table */}
            <Box
                sx={{
                    border: `1px solid ${GT_COLORS.border}`,
                    borderRadius: '10px',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${GT_COLORS.borderLight}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography fontSize={14} fontWeight={600} color={GT_COLORS.textPrimary}>
                        Preview ({tableRows.length} rows)
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadPreview}
                        sx={{ textTransform: 'none', fontSize: 13 }}
                    >
                        Download Preview
                    </Button>
                </Box>

                <DenseTable
                    columns={columns}
                    rows={tableRows}
                    getRowKey={(row) => row._key}
                    pageSize={100}
                    defaultSortColumn="rowNumber"
                    defaultSortDir="asc"
                    rowClassName={(row) => {
                        if (row.status === 'validation-error') return 'bulk-update-error-row';
                        if (row.status === 'unmatched' || row.status === 'duplicate') return 'bulk-update-unmatched-row';
                        return undefined;
                    }}
                />

                <style>{`
                    .bulk-update-error-row { background-color: ${GT_COLORS.redBg} !important; }
                    .bulk-update-unmatched-row { background-color: ${GT_COLORS.headerBg} !important; }
                `}</style>
            </Box>

            {/* Action buttons */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                >
                    Back to Upload
                </Button>

                <Stack direction="row" spacing={2} alignItems="center">
                    {estimate && (
                        <Typography fontSize={13} color={GT_COLORS.textMuted}>
                            Estimated time: {estimate}
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!canContinue}
                        onClick={onContinue}
                        sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                        Continue ({summary.readyToUpdateCount} user{summary.readyToUpdateCount !== 1 ? 's' : ''} to update)
                    </Button>
                </Stack>
            </Stack>
        </Stack>
    );
};
